import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type {
  Beleg,
  Knoten,
  PlanKnoten,
  UnteragentKnoten,
  WerkzeugEintrag,
  WerkzeugKnoten,
} from "./typen";
import { ART_LABELS, RISIKO_LABELS, kurzfehler, werkzeugLabel } from "./texte";
import {
  ZeichenHaken,
  ZeichenKasten,
  ZeichenKreuz,
  ZeichenLaeuft,
  ZeichenUebersprungen,
  ZeichenWinkel,
} from "./Zeichen";

/**
 * The work trace.
 *
 * This is the panel's claim to being trustworthy, and the design follows from one
 * observation: a run with twenty lookups is unreadable if each one takes four lines,
 * and worthless if none of them can be opened. So every step is one line by default
 * carrying the tool's own German summary of what it found, and every line opens onto
 * exactly what was asked and exactly what came back.
 *
 * The whole trace hangs off a rail in --agent-accent. That token is deliberately not
 * the product accent: on any screen in this application, that colour means a machine
 * produced it.
 */

interface SpurProps {
  knoten: Knoten[];
  /** The tool catalogue, for module and risk in the expanded body. */
  katalog: Map<string, WerkzeugEintrag>;
  /** Rendered where a `freigabe` node appears — the approval card, injected by the panel. */
  freigabe: (freigabeId: string | null, titel: string) => ReactNode;
  /** Nesting depth. Sub-agent children indent once and no further. */
  tiefe?: number;
}

export function Spur({ knoten, katalog, freigabe, tiefe = 0 }: SpurProps) {
  if (knoten.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-[4px] pl-[8px] border-l-2"
      style={{ borderColor: tiefe === 0 ? "var(--agent-accent)" : "var(--line-hairline)" }}
    >
      {knoten.map((k) => {
        switch (k.art) {
          case "text":
            // Interim narration between tool calls. The final answer is rendered by the
            // panel below the trace; this is the model thinking out loud mid-run.
            return (
              <p
                key={k.schluessel}
                className="text-[length:var(--text-xs)]"
                style={{ color: "var(--text-secondary)", lineHeight: "var(--leading-snug)" }}
              >
                {k.text}
              </p>
            );

          case "notiz":
            return (
              <p
                key={k.schluessel}
                className="text-[length:var(--text-2xs)]"
                style={{ color: "var(--text-muted)", fontStyle: k.leise ? "italic" : undefined }}
              >
                {k.text}
              </p>
            );

          case "plan":
            return <Planliste key={k.schluessel} plan={k} />;

          case "werkzeug":
            return <Werkzeugzeile key={k.schluessel} knoten={k} katalog={katalog} />;

          case "unteragent":
            return (
              <Unteragentzeile
                key={k.schluessel}
                knoten={k}
                katalog={katalog}
                freigabe={freigabe}
                tiefe={tiefe}
              />
            );

          case "freigabe":
            return <div key={k.schluessel}>{freigabe(k.freigabeId, k.titel)}</div>;

          case "fehler":
            return (
              <p
                key={k.schluessel}
                className="flex items-start gap-[4px] text-[length:var(--text-xs)]"
                style={{ color: "var(--status-bad-tx)" }}
              >
                <span className="mt-[2px] shrink-0">
                  <ZeichenKreuz />
                </span>
                {/* One line here; the untruncated original belongs to the failure
                    block at the end of the turn, not to a step in the trace. */}
                <span title={k.text}>{kurzfehler(k.text)}</span>
              </p>
            );
        }
      })}
    </div>
  );
}

/**
 * The plan.
 *
 * Kept short on purpose — a plan longer than the panel is a plan nobody reads. Steps
 * tick off in place, which is most of what makes a multi-step run feel like it is
 * under control rather than merely busy.
 */
function Planliste({ plan }: { plan: PlanKnoten }) {
  const erledigt = plan.schritte.filter((s) => s.status === "erledigt").length;

  return (
    <section
      className="border px-[8px] py-[6px] rounded-[var(--radius-2)]"
      style={{ borderColor: "var(--line-hairline)", background: "var(--surface-sunken)" }}
      aria-label="Arbeitsplan des Assistenten"
    >
      <header className="flex items-baseline gap-[6px] mb-[4px]">
        <span
          className="text-[length:var(--text-3xs)]"
          style={{
            color: "var(--text-caps)",
            textTransform: "var(--label-transform)" as never,
            letterSpacing: "var(--tracking-caps)",
            fontWeight: "var(--weight-medium)",
          }}
        >
          {plan.titel ?? "Vorgehen"}
        </span>
        <span className="num ml-auto text-[length:var(--text-3xs)]" style={{ color: "var(--text-muted)" }}>
          {erledigt} von {plan.schritte.length}
        </span>
      </header>

      <ol className="flex flex-col gap-[2px]">
        {plan.schritte.map((schritt, n) => {
          const abgehakt = schritt.status === "erledigt" || schritt.status === "uebersprungen";
          return (
            <li
              key={`${n}-${schritt.text}`}
              className="flex items-start gap-[6px] text-[length:var(--text-xs)]"
              style={{
                color: abgehakt ? "var(--text-muted)" : "var(--text-primary)",
                textDecoration: abgehakt ? "line-through" : undefined,
              }}
            >
              <span className="mt-[2px] shrink-0" style={{ color: schrittFarbe(schritt.status) }}>
                {schritt.status === "erledigt" ? (
                  <ZeichenHaken />
                ) : schritt.status === "uebersprungen" ? (
                  <ZeichenUebersprungen />
                ) : schritt.status === "laeuft" ? (
                  <ZeichenLaeuft />
                ) : schritt.status === "gescheitert" ? (
                  <ZeichenKreuz />
                ) : (
                  <ZeichenKasten />
                )}
              </span>
              <span>{schritt.text}</span>
              {/* The state has to survive greyscale and a screen reader, so the shape
                  is never the only carrier of it. */}
              <span className="sr-only">{schrittWort(schritt.status)}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function schrittFarbe(status: string): string {
  if (status === "erledigt") return "var(--status-ok-tx)";
  if (status === "gescheitert") return "var(--status-bad-tx)";
  if (status === "laeuft") return "var(--agent-accent)";
  return "var(--text-muted)";
}

function schrittWort(status: string): string {
  if (status === "erledigt") return "erledigt";
  if (status === "uebersprungen") return "übersprungen, nicht zutreffend";
  if (status === "gescheitert") return "gescheitert";
  if (status === "laeuft") return "läuft";
  return "offen";
}

/** One tool call: the summary always, the evidence on demand. */
function Werkzeugzeile({
  knoten,
  katalog,
}: {
  knoten: WerkzeugKnoten;
  katalog: Map<string, WerkzeugEintrag>;
}) {
  const [offen, setOffen] = useState(false);
  const eintrag = katalog.get(knoten.name);
  const risiko = knoten.risikoklasse && knoten.risikoklasse !== "frei"
    ? RISIKO_LABELS[knoten.risikoklasse] ?? knoten.risikoklasse
    : null;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOffen((v) => !v)}
        aria-expanded={offen}
        className="flex items-start gap-[4px] text-left w-full py-[1px]"
      >
        <span
          className="mt-[2px] shrink-0 transition-transform duration-[var(--dur-1)] ease-[var(--ease-standard)]"
          style={{
            color: "var(--text-muted)",
            transform: offen ? "rotate(90deg)" : undefined,
          }}
        >
          <ZeichenWinkel />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-[4px]">
            <span
              className="text-[length:var(--text-xs)]"
              style={{ color: "var(--text-primary)", fontWeight: "var(--weight-medium)" }}
            >
              {werkzeugLabel(knoten.name)}
            </span>
            {knoten.status === "laeuft" ? (
              <span className="text-[length:var(--text-3xs)]" style={{ color: "var(--text-muted)" }}>
                läuft
              </span>
            ) : null}
            {risiko ? (
              <span
                className="text-[length:var(--text-3xs)] px-[2px] border-l-2"
                style={{ color: "var(--status-warn-tx)", borderColor: "var(--status-warn-bar)" }}
              >
                {risiko}
              </span>
            ) : null}
          </span>

          {knoten.ergebnis ? (
            <span
              className="block text-[length:var(--text-xs)]"
              style={{ color: "var(--text-secondary)", lineHeight: "var(--leading-snug)" }}
            >
              {knoten.ergebnis}
            </span>
          ) : null}
        </span>
      </button>

      {offen ? (
        <div className="pl-[16px] pt-[4px] pb-[6px] flex flex-col gap-[6px]">
          {eintrag ? (
            <p className="text-[length:var(--text-2xs)]" style={{ color: "var(--text-muted)" }}>
              <span className="mono">{knoten.name}</span> · {eintrag.modul} ·{" "}
              {ART_LABELS[eintrag.art] ?? eintrag.art}
            </p>
          ) : (
            <p className="mono text-[length:var(--text-2xs)]" style={{ color: "var(--text-muted)" }}>
              {knoten.name}
            </p>
          )}

          <Feldblock titel="Angefragt">
            <pre
              className="mono text-[length:var(--text-2xs)] whitespace-pre-wrap break-words"
              style={{ color: "var(--text-primary)" }}
            >
              {formatiereEingabe(knoten.eingabe)}
            </pre>
          </Feldblock>

          <Feldblock titel="Zurückgegeben">
            <p className="text-[length:var(--text-xs)]" style={{ color: "var(--text-primary)" }}>
              {knoten.ergebnis ?? "Noch kein Ergebnis."}
            </p>
          </Feldblock>

          <Belegleiste belege={knoten.belege} />
        </div>
      ) : null}
    </div>
  );
}

function Feldblock({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div
      className="border px-[6px] py-[4px] rounded-[var(--radius-1)]"
      style={{ borderColor: "var(--line-hairline)", background: "var(--surface-sunken)" }}
    >
      <p
        className="text-[length:var(--text-3xs)] mb-[2px]"
        style={{
          color: "var(--text-caps)",
          textTransform: "var(--label-transform)" as never,
          letterSpacing: "var(--tracking-caps)",
        }}
      >
        {titel}
      </p>
      {children}
    </div>
  );
}

/**
 * Provenance.
 *
 * Every record a step touched, as a link into the application. This is the line that
 * separates a figure a Verwalter can defend from a figure they merely repeated.
 */
export function Belegleiste({ belege }: { belege: Beleg[] }) {
  if (belege.length === 0) return null;

  return (
    <div className="flex flex-col gap-[2px]">
      <p
        className="text-[length:var(--text-3xs)]"
        style={{
          color: "var(--text-caps)",
          textTransform: "var(--label-transform)" as never,
          letterSpacing: "var(--tracking-caps)",
        }}
      >
        Belege
      </p>
      <ul className="flex flex-col gap-[1px]">
        {belege.map((beleg) => (
          <li key={`${beleg.href}-${beleg.label}`}>
            <Link
              to={beleg.href}
              className="text-[length:var(--text-xs)] underline underline-offset-2"
              style={{ color: "var(--text-link)" }}
            >
              {beleg.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A delegated batch.
 *
 * Collapsed by default and counted, so a fan-out over six properties is one line the
 * user can open when a number looks wrong, rather than sixty lines they have to scroll
 * past every time it looks right.
 */
function Unteragentzeile({
  knoten,
  katalog,
  freigabe,
  tiefe,
}: {
  knoten: UnteragentKnoten;
  katalog: Map<string, WerkzeugEintrag>;
  freigabe: SpurProps["freigabe"];
  tiefe: number;
}) {
  const [offen, setOffen] = useState(false);
  const anzahl = knoten.teilauftraege.length;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOffen((v) => !v)}
        aria-expanded={offen}
        className="flex items-start gap-[4px] text-left w-full py-[1px]"
      >
        <span
          className="mt-[2px] shrink-0 transition-transform duration-[var(--dur-1)] ease-[var(--ease-standard)]"
          style={{ color: "var(--text-muted)", transform: offen ? "rotate(90deg)" : undefined }}
        >
          <ZeichenWinkel />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-[4px] flex-wrap">
            <span
              className="text-[length:var(--text-xs)]"
              style={{ color: "var(--text-primary)", fontWeight: "var(--weight-medium)" }}
            >
              {knoten.titel}
            </span>
            {anzahl > 1 ? (
              <span
                className="num text-[length:var(--text-3xs)]"
                style={{ color: "var(--text-muted)" }}
              >
                {anzahl} parallel
              </span>
            ) : null}
            <span
              className="text-[length:var(--text-3xs)]"
              style={{
                color:
                  knoten.status === "fehler"
                    ? "var(--status-bad-tx)"
                    : knoten.status === "fertig"
                      ? "var(--status-ok-tx)"
                      : "var(--text-muted)",
              }}
            >
              {knoten.status === "fehler"
                ? "gescheitert"
                : knoten.status === "fertig"
                  ? "fertig"
                  : "läuft"}
            </span>
          </span>

          {knoten.zusammenfassung ? (
            <span
              className="block text-[length:var(--text-xs)]"
              style={{ color: "var(--text-secondary)", lineHeight: "var(--leading-snug)" }}
            >
              {knoten.zusammenfassung}
            </span>
          ) : null}
        </span>
      </button>

      {offen ? (
        <div className="pl-[12px] pt-[4px] flex flex-col gap-[4px]">
          {knoten.teilauftraege.map((auftrag, n) => (
            <Feldblock key={`${n}-${auftrag.titel}`} titel={auftrag.titel}>
              <p
                className="text-[length:var(--text-xs)]"
                style={{ color: "var(--text-primary)", lineHeight: "var(--leading-snug)" }}
              >
                {auftrag.auftrag ?? "Kein Auftragstext aufgezeichnet."}
              </p>
            </Feldblock>
          ))}

          {knoten.kinder.length > 0 ? (
            <Spur knoten={knoten.kinder} katalog={katalog} freigabe={freigabe} tiefe={tiefe + 1} />
          ) : (
            <p className="text-[length:var(--text-2xs)]" style={{ color: "var(--text-muted)" }}>
              Jeder Teilauftrag lief als eigener Lauf mit eigenem Budget und durfte nur lesen.
              Gezeigt wird, was zurückgemeldet wurde.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function formatiereEingabe(eingabe: unknown): string {
  if (eingabe === null || eingabe === undefined) return "keine Parameter";
  if (typeof eingabe === "string") return eingabe;
  try {
    const text = JSON.stringify(eingabe, null, 2);
    return text.length > 1200 ? `${text.slice(0, 1200)}…` : text;
  } catch {
    return String(eingabe);
  }
}
